import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  Database,
  Layers,
  Banknote,
  AlertTriangle,
  BarChart3,
  FileText,
  ShieldCheck,
  GitBranch,
  Sparkles,
  CheckCircle2,
  Scissors,
  ShoppingCart,
  Target,
  Award,
  Home,
} from "lucide-react";
import PublicLayout from "../../components/public/PublicLayout";
import LeadModal from "../../components/public/LeadModal";
import { IconVisual } from "./MarketingHome";

export default function ForArnHolders() {
  const [leadOpen, setLeadOpen] = useState(false);

  return (
    <PublicLayout title="For MFD · ARN Holders">
      <Hero onConnect={() => setLeadOpen(true)} />
      <CapabilityGroups />
      <TractionStrip />
      <TrustGroups />
      <PricingTeaser />
      <ClosingCTA onConnect={() => setLeadOpen(true)} />
      {leadOpen && (
        <LeadModal
          onClose={() => setLeadOpen(false)}
          source="mfd_page"
          title="Let's walk through Kinntegra together."
          subtitle="A short call from our team — we'll show you how each module fits your practice."
        />
      )}
    </PublicLayout>
  );
}

/* ─────────────────── HERO ─────────────────── */
function Hero({ onConnect }) {
  return (
    <section data-testid="mfd-hero" className="relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-40 -right-40 h-[520px] w-[520px] rounded-full bg-[#C8A96B]/[0.06] blur-[160px]" />
      </div>
      <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pt-6 md:pt-10 pb-16 md:pb-20">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/5 border border-white/10 text-[11px] tracking-[0.2em] uppercase text-white/70 mb-8">
              <Sparkles size={12} className="text-[#C8A96B]" />
              For MFDs &amp; ARN holders
            </div>
            <h1 className="font-light text-[40px] leading-[1.06] sm:text-[56px] lg:text-[68px] tracking-[-0.025em]">
              The reporting engine your
              <br />
              <span className="font-normal italic text-[#C8A96B]">
                back-office can&apos;t build.
              </span>
            </h1>
            <p className="mt-10 text-lg md:text-xl text-white/70 max-w-2xl leading-[1.55] font-light">
              Stop stitching together Karvy CSVs, CAMS PDFs and Excel pivots.
              Run your entire practice on one platform — built by an ARN
              holder who got tired of doing exactly that.
            </p>
            <div className="mt-12 flex flex-wrap gap-3">
              <Link
                to="/start-trial?plan=pro"
                data-testid="mfd-hero-cta-trial"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-[#C8A96B] text-[#07111F] font-medium hover:bg-[#d4b878] transition active:scale-[0.98]"
              >
                Start 14-day free trial
                <ArrowUpRight size={16} />
              </Link>
              <button
                onClick={onConnect}
                data-testid="mfd-hero-cta"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full border border-white/15 text-white/85 hover:bg-white/[0.04] transition"
              >
                Book a 1:1 demo
              </button>
              <Link
                to="/pricing"
                data-testid="mfd-hero-cta-pricing"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full border border-white/15 text-white/85 hover:bg-white/[0.04] transition"
              >
                See pricing
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5">
            <IconVisual
              items={[
                { icon: Database, label: "Karvy + CAMS" },
                { icon: Layers, label: "PAN drill-down" },
                { icon: Banknote, label: "Brokerage" },
                { icon: Scissors, label: "Provisional Sell" },
                { icon: ShoppingCart, label: "Provisional Buy" },
                { icon: Target, label: "Profit Realignment" },
                { icon: Home, label: "Family View" },
                { icon: AlertTriangle, label: "KYC Mismatch" },
                { icon: FileText, label: "Client PDFs" },
              ]}
              eyebrow="What you get"
              caption="Same platform we run our own ₹1,000+ Cr practice on — turn-key for yours."
              testId="mfd-hero-image"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────── CAPABILITY GROUPS ─────────────────── */
function CapabilityGroups() {
  const items = [
    {
      icon: Database,
      title: "Karvy + CAMS auto-ingest",
      copy: "WBR77, MFSD205, WBR2, WBR9, MFSD201 emails parsed on arrival. Your investor / AUM / transaction masters update overnight. Zero spreadsheet work.",
    },
    {
      icon: Layers,
      title: "PAN → Folio → Scheme drill-down",
      copy: "Every dashboard rolls up by PAN first (with name fallback). Click a client → see their folios. Click a folio → see their schemes. No more chasing data across 12 tabs.",
    },
    {
      icon: Banknote,
      title: "Unpaid Brokerage reconciliation",
      copy: "Folios in active_units vs. WBR77 / MFSD205 — instantly see who you've never been paid for. Drill down by transaction number. We catch what your back-office misses.",
    },
    {
      icon: AlertTriangle,
      title: "KYC Mismatch + Reinvestment audit",
      copy: "Cross-reference Karvy vs CAMS data per PAN — flag mismatches in name, address, bank, IDCW. Audit reinvestment tags for hidden multi-folio entries.",
    },
    {
      icon: BarChart3,
      title: "Profit Realignment + Exit-Free Lots",
      copy: "LTCG / STCG tax-harvesting tools across families. Identify exit-free lots by scheme / folio / bucket with expandable rows. Built for serious tax planning.",
    },
    {
      icon: FileText,
      title: "Client-ready PDFs",
      copy: "Account opening forms, LOEs, family AUM reports, capital-gains statements. White-labeled to your firm in the Enterprise plan.",
    },
    {
      icon: Scissors,
      title: "Provisional Sell — tax-aware exits",
      copy: "Simulate redemptions across folios per client BEFORE you press the button. LTCG / STCG impact computed lot-by-lot. Pair with the ₹1.25 L LTCG window to harvest gains tax-free every March.",
    },
    {
      icon: ShoppingCart,
      title: "Provisional Buy — strategic allocations",
      copy: "Tell the platform how much to deploy. We split it across Wealth / Thematic / Foreign sleeves at AMC-weighted percentages with Liquid auto-populated per AMC. Run the math once, commit when ready.",
    },
    {
      icon: Target,
      title: "Profit Realignment — lock gains, reset the clock",
      copy: "Find every position where LTCG can be booked tax-free, then redeploy into the same or a different scheme. Holding-period clock resets, market exposure stays — the most under-used tax move in MF distribution.",
    },
    {
      icon: Award,
      title: "Exit-Free Lots — what's redeemable today",
      copy: "Every active lot grouped by scheme / folio / bucket, expandable rows showing crossing of the 12-month LTCG threshold and remaining ₹1.25 L tax-free headroom this FY.",
    },
    {
      icon: Home,
      title: "Family View — manage entire households on one screen",
      copy: "Self, spouse, parents, children, joint and minor holdings — grouped under one family unit. Combined AUM, combined XIRR, combined goal-progress. One-click switch back to individual view.",
    },
  ];
  return (
    <section
      data-testid="mfd-capabilities"
      className="py-16 md:py-20 border-y border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <Eyebrow>Everything Kinntegra runs on</Eyebrow>
        <h2 className="mt-5 text-3xl sm:text-4xl md:text-5xl font-light tracking-[-0.02em] max-w-3xl leading-[1.15]">
          Same platform we use — now yours.
        </h2>
        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-[1px] bg-white/5 rounded-2xl overflow-hidden border border-white/5">
          {items.map((it) => (
            <FeatureCard key={it.title} {...it} />
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────── TRACTION STRIP ─────────────────── */
function TractionStrip() {
  const labels = [
    { v: "₹1,000+ Cr", l: "AUM we run on Kinntegra" },
    { v: "1.6 M", l: "Reconciled rows tracked" },
    { v: "5", l: "Broker feeds unified" },
    { v: "14-day", l: "Free trial · no card" },
  ];
  return (
    <section className="py-12 md:py-16 border-b border-white/5" data-testid="mfd-strip">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {labels.map((s) => (
            <div key={s.l}>
              <div className="text-3xl md:text-4xl font-light text-white tabular-nums">
                {s.v}
              </div>
              <div className="mt-2 text-xs uppercase tracking-[0.18em] text-white/45">
                {s.l}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────── TRUST GROUPS ─────────────────── */
function TrustGroups() {
  const items = [
    {
      icon: ShieldCheck,
      title: "Complete data isolation",
      copy: "Your investor data, transactions and AUM live behind a per-tenant filter. No other ARN holder can ever see your rows.",
    },
    {
      icon: GitBranch,
      title: "Auto-updated platform",
      copy: "Every feature and bug fix we ship goes live for you instantly. No upgrade cycles, no version drift, no migrations.",
    },
    {
      icon: Sparkles,
      title: "Built by an ARN holder",
      copy: "Kinntegra is what we run our own ₹1,000+ Cr practice on. You get the same tool — not a watered-down SaaS.",
    },
  ];
  return (
    <section
      data-testid="mfd-trust"
      className="py-16 md:py-20 border-b border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <Eyebrow>Why your data stays yours</Eyebrow>
        <h2 className="mt-5 text-3xl sm:text-4xl md:text-5xl font-light tracking-[-0.02em] max-w-3xl leading-[1.15]">
          Independent firms. Shared infrastructure.
        </h2>
        <div className="mt-12 grid md:grid-cols-3 gap-[1px] bg-white/5 rounded-2xl overflow-hidden border border-white/5">
          {items.map((it) => (
            <FeatureCard key={it.title} {...it} />
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────── PRICING TEASER ─────────────────── */
function PricingTeaser() {
  const tiers = [
    { name: "Starter",    price: "₹4,999",  sub: "Up to 250 clients · ₹25 Cr AUM" },
    { name: "Pro",        price: "₹8,999",  sub: "Up to 1,000 clients · ₹150 Cr AUM", featured: true },
    { name: "Enterprise", price: "₹16,999", sub: "Unlimited · white-label" },
  ];
  return (
    <section className="py-16 md:py-20 border-b border-white/5" data-testid="mfd-pricing-teaser">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-12">
          <div>
            <Eyebrow>Plans built for distribution firms</Eyebrow>
            <h2 className="mt-5 text-3xl sm:text-4xl md:text-5xl font-light tracking-[-0.02em] max-w-3xl leading-[1.15]">
              Simple, annual-billed pricing.
            </h2>
          </div>
          <Link
            to="/pricing"
            data-testid="mfd-pricing-link"
            className="text-sm text-[#C8A96B] hover:text-[#d4b878] inline-flex items-center gap-1.5"
          >
            See full feature comparison <ArrowUpRight size={14} />
          </Link>
        </div>
        <div className="grid md:grid-cols-3 gap-[1px] bg-white/5 rounded-2xl overflow-hidden border border-white/5">
          {tiers.map((t) => (
            <div
              key={t.name}
              data-testid={`mfd-tier-${t.name.toLowerCase()}`}
              className={`relative bg-[#07111F] p-8 md:p-10 transition ${t.featured ? "bg-gradient-to-b from-[#C8A96B]/[0.06] to-transparent" : "hover:bg-[#0c1827]"}`}
            >
              {t.featured && (
                <div className="absolute top-5 right-5 text-[10px] uppercase tracking-[0.18em] text-[#C8A96B] border border-[#C8A96B]/40 rounded-full px-2 py-0.5">
                  Most popular
                </div>
              )}
              <div className="text-xs uppercase tracking-[0.18em] text-white/45 mb-3">{t.name}</div>
              <div className="flex items-baseline gap-1">
                <span className="text-4xl md:text-5xl font-light text-white tabular-nums">{t.price}</span>
                <span className="text-white/40 text-sm">/ month</span>
              </div>
              <p className="text-sm text-white/55 mt-4 leading-relaxed">{t.sub}</p>
              <Link
                to={`/pricing#${t.name.toLowerCase()}`}
                className="mt-6 inline-flex items-center gap-1.5 text-sm text-[#C8A96B] hover:text-[#d4b878]"
              >
                Explore {t.name} <ArrowUpRight size={14} />
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────── CLOSING CTA ─────────────────── */
function ClosingCTA({ onConnect }) {
  return (
    <section
      data-testid="mfd-cta"
      className="py-20 md:py-24 relative overflow-hidden"
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 h-[320px] w-[640px] rounded-full bg-[#C8A96B]/[0.05] blur-[120px]" />
      </div>
      <div className="relative max-w-3xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-light tracking-[-0.02em] leading-[1.15]">
          See your numbers in one place.
        </h2>
        <p className="mt-6 text-base md:text-lg text-white/55 font-light leading-[1.7] max-w-xl mx-auto">
          We&apos;ll walk you through Kinntegra against your actual Karvy /
          CAMS feed, AUM size, and operating cadence — in 30 minutes.
        </p>
        <div className="mt-10 flex justify-center flex-wrap gap-3">
          <button
            onClick={onConnect}
            data-testid="mfd-cta-final"
            className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full bg-[#C8A96B] text-[#07111F] font-medium hover:bg-[#d4b878] transition"
          >
            Schedule a Call
            <ArrowUpRight size={16} />
          </button>
          <Link
            to="/pricing"
            className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full border border-white/15 text-white/85 hover:bg-white/[0.04] transition"
          >
            See pricing
          </Link>
        </div>
        <div className="mt-6 inline-flex items-center gap-2 text-xs text-white/45">
          <CheckCircle2 size={14} className="text-[#C8A96B]" />
          14-day free trial · no credit card · cancel anytime
        </div>
      </div>
    </section>
  );
}

/* ─────────────────── shared ─────────────────── */
function FeatureCard({ icon: Icon, title, copy }) {
  return (
    <div
      data-testid={`mfd-card-${title.slice(0, 14).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
      className="group bg-[#07111F] p-8 md:p-10 hover:bg-[#0c1827] transition relative overflow-hidden"
    >
      <div className="absolute inset-x-0 top-0 h-[1px] bg-gradient-to-r from-transparent via-[#C8A96B]/40 to-transparent opacity-0 group-hover:opacity-100 transition" />
      <Icon size={22} className="text-[#C8A96B] mb-7" strokeWidth={1.4} />
      <h3 className="text-lg font-light text-white mb-3 tracking-tight leading-snug">
        {title}
      </h3>
      <p className="text-sm text-white/55 leading-relaxed">{copy}</p>
    </div>
  );
}

function Eyebrow({ children }) {
  return (
    <div className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.22em] text-white/40">
      <span className="h-[1px] w-8 bg-[#C8A96B]/60" />
      {children}
    </div>
  );
}
